import React, { useState, useRef, useEffect, useMemo } from "react";
import { Stack, Typography, ToggleButtonGroup, ToggleButton, IconButton, Box, useTheme, useMediaQuery } from "@mui/material";
import { SectionTitleWithExp } from "../customTypo/SectionTitleWithExp";
import { CardContainerRetro } from "../Container/CardContainerRetro";
import BarChartDate from "../../chart/BarChartDate";
import colors from "../../designTokens/colors.esm";
import { Section } from "../Container/Section";
import colorsCustom from "../../designTokens/colorsCustom";
import { useTranslation } from "react-i18next";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import DateRangedThreadModal from "../modal/DateRangedThreadModal";
import { trackEvent } from "../../utils/analytics";
import { useKeyMetricChartData } from "../../hooks/useThreadsSelector";
import { ChartSkeleton } from "../skeleton/ChartSkeleton";
import { DataLoadError } from "../error/DataLoadError";


export const LifetimeKeyMetricChartSection = ({
  getThreadsForDateRange,
}) => {
  // ThreadsContext에서 차트 데이터 가져오기
  const { 
    lifetimeInsight: insight,
    isInsightsLoading,
    isDataReady,
    error 
  } = useKeyMetricChartData();
  const { t } = useTranslation();
  const theme = useTheme();
  
  // 반응형 Hook
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));



  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedThreads, setSelectedThreads] = useState([]);

  const [dateUnit, setDateUnit] = useState("year");
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [groupedData, setGroupedData] = useState([]);

  const [modalTitle, setModalTitle] = useState(
    "Threads for Selected Date Range"
  );

  const barChartContainerRef = useRef(null);

  // profileViews 배열을 메모이제이션하여 불필요한 리렌더링 방지
  const profileViews = useMemo(() => {
    const views = insight?.profileViews || [];
    return views;
  }, [insight?.profileViews]);

  const handleBarClick = (clickedDate) => {
    try {
      // 클릭한 날짜와 그 전날의 데이터를 가져옴
      const endDate = new Date(clickedDate);
      endDate.setHours(23, 59, 59, 999);
      
      const startDate = new Date(clickedDate);
      startDate.setDate(startDate.getDate() - 1); // 전날
      startDate.setHours(0, 0, 0, 0);

      const threads = getThreadsForDateRange(startDate, endDate);

      // threads가 유효한 경우에만 상태 업데이트
      if (Array.isArray(threads)) {
        setSelectedThreads(threads);
        setModalTitle(
          `Threads from ${startDate.toLocaleDateString(undefined, {
            month: "2-digit",
            day: "2-digit",
          })} to ${endDate.toLocaleDateString(undefined, {
            month: "2-digit",
            day: "2-digit",
          })}`
        );
        setIsModalOpen(true);
        trackEvent("date_ranged_thread_modal", {
          dateUnit: dateUnit,
        });
      }
    } catch (error) {
      console.error("Error in handleBarClick:", error);
    }
  };

  // 데이터 그룹화
  useEffect(() => {
    const groupData = () => {
      if (!profileViews || profileViews.length === 0) {
        setGroupedData([]);
        setCurrentGroupIndex(0);
        return;
      }

      const grouped = profileViews.reduce((acc, item) => {
        const date = new Date(item.x);
        const key = dateUnit === "year"
          ? date.getFullYear().toString()
          : `${date.getFullYear()}-${date.getMonth()}`; // 연도+월 조합으로 그룹화
        if (!acc[key]) acc[key] = [];
        // Create a clean copy of the item to avoid circular references
        acc[key].push({ x: item.x, y: item.y });
        return acc;
      }, {});

      // 각 그룹 내에서도 날짜순으로 정렬
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => new Date(a.x) - new Date(b.x));
      });

      let groupedArray = Object.entries(grouped).map(([key, data]) => ({
        key,
        data,
        sortDate: new Date(data[0].x) // 정렬용 날짜
      }));

      // Sort the grouped array in descending order (최신 순)
      groupedArray = groupedArray.sort((a, b) => {
        return b.sortDate - a.sortDate; // Sort descending
      });

      // 데이터만 추출하여 설정
      const sortedData = groupedArray.map(group => group.data);

      setGroupedData(sortedData);
      setCurrentGroupIndex(0); // 0번째 인덱스가 가장 최근
    };

    groupData();
  }, [profileViews, dateUnit]);

  const handleNextGroup = () => {
    if (currentGroupIndex > 0) {
      setCurrentGroupIndex(currentGroupIndex - 1);
    }
  };

  const handlePreviousGroup = () => {
    if (currentGroupIndex < groupedData.length - 1) {
      setCurrentGroupIndex(currentGroupIndex + 1);
    }
  };

  // 로딩 중
  if (isInsightsLoading || !isDataReady) {
    return (
      <ChartSkeleton 
        title={t("lifetime.engagement.title")}
      />
    );
  }

  // 데이터 로딩 실패
  if (error && !insight) {
    return (
      <Section>
        <SectionTitleWithExp
          title={t("lifetime.engagement.title")}
        />
        <CardContainerRetro
          p={isMobile ? "24px" : "32px"}
          bg={colorsCustom.paper}
          weight="bold"
          rd={16}
        >
          <DataLoadError
            title={t('error.chartDataFailed')}
            message={t('error.threadsDataLoadFailed')}
            showRetry={false}
          />
        </CardContainerRetro>
      </Section>
    );
  }

  return (
    <Section>
      <SectionTitleWithExp
        title={t("lifetime.engagement.title")}
      />
      <CardContainerRetro
        p={isMobile ? "16px" : "24px 32px 48px 24px"}
        bg={colorsCustom.paper}
        weight="bold"
        rd={16}
        style={{ position: "relative" }}
      >
        <Stack spacing={2} sx={{ width: "100%" }}>
          <Typography variant={isMobile ? "body2" : "body1"}>
            {t("lifetime.engagement.profileView.description")}
          </Typography>

          <Stack
            direction="row"
            sx={{
              width: "100%",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <ToggleButtonGroup
              value={dateUnit}
              exclusive
              onChange={(_, value) => {
                if (value !== null) {
                  setDateUnit(value);
                  trackEvent("change_date_unit", {
                    dateUnit: value,
                  });
                }
              }}
              size={isMobile ? "small" : "medium"}
            >
              <ToggleButton value="year">Year</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
            </ToggleButtonGroup>
              <Box
              sx={{
                width: "1px",
                height: "24px",
                  backgroundColor: colors.gray[3],
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                size={isMobile ? "small" : "large"}
                onClick={() => {
                  handlePreviousGroup();
                  trackEvent("change_group", {
                    direction: "previous",
                  });
                }}
                disabled={currentGroupIndex === groupedData.length - 1}
              >
                <IconChevronLeft />
              </IconButton>
              <Typography
                variant={isMobile ? "body1" : "h6"}
                fontWeight="bold"
                sx={{
                  textAlign: "center",
                  minWidth: "80px",
                }}
              >
                {dateUnit === "year"
                  ? new Date(
                      groupedData[currentGroupIndex]?.[0]?.x
                    ).getFullYear()
                  : `${new Date(
                      groupedData[currentGroupIndex]?.[0]?.x
                    ).toLocaleString("default", {
                      month: "short",
                    })} ${new Date(
                      groupedData[currentGroupIndex]?.[0]?.x
                    ).getFullYear()}`}
              </Typography>
              <IconButton
                size={isMobile ? "small" : "large"}
                onClick={() => {
                  handleNextGroup();
                  trackEvent("change_group", {
                    direction: "next",
                  });
                }}
                disabled={currentGroupIndex === 0}
              >
                <IconChevronRight />
              </IconButton>
            </Stack>
          </Stack>
          <Box
            ref={barChartContainerRef}
            sx={{
              width: "100%",
              minHeight: isMobile ? 240 : 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {profileViews.length === 0 ? (
              <Typography
                variant="body2"
                color="text.disabled"
                textAlign="center"
                sx={{ py: 4, width: "100%" }}
              >
                No profile view data available
              </Typography>
            ) : groupedData.length === 0 ? (
              <Typography
                variant="body2"
                color="text.disabled"
                textAlign="center"
                sx={{ py: 4, width: "100%" }}
              >
                Processing chart data...
              </Typography>
            ) : !groupedData[currentGroupIndex] || groupedData[currentGroupIndex].length === 0 ? (
              <Typography
                variant="body2"
                color="text.disabled"
                textAlign="center"
                sx={{ py: 4, width: "100%" }}
              >
                No data for selected period
              </Typography>
            ) : (
              <BarChartDate
                data={groupedData[currentGroupIndex]}
                width="100%"
                height={isMobile ? 240 : 320}
                isMobile={isMobile}
                dateFormat="%b %d"
                fillColor={colors.gray[8]}
                axisColor={colors.gray[3]}
                tickColor={colors.gray[5]}
                xTicks={isMobile ? 3 : 5}
                yTicks={isMobile ? 3 : 4}
                onBarClick={handleBarClick}
              />
            )}
          </Box>
        </Stack>
      </CardContainerRetro>
      <DateRangedThreadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        threads={selectedThreads}
      />
    </Section>
  );
};